/**
 * Las pantallas de la estación producción, del boceto validado por la gerenta
 * el 7/9 (`../Perejil/public/poc/produccion.html`) con la vuelta de tuerca que
 * pidió Nicolás el 10/9 al verlo andar: el inicio son BOTONES por acción, y el
 * peso del silo se toma donde de verdad se pesa. Abrir un silo queda como en el
 * boceto; **cerrar pesa y cierra**; **cambio de turno pesa los silos abiertos**
 * —uno o dos— y los deja abiertos para quien viene; bajada de carro y parada.
 * Tocar la tarjeta de un silo es cerrarlo. El boceto hacía que tocar el silo
 * fuera «entregar el turno» y escondía el peso detrás de ese verbo; en el wire
 * son los mismos comandos (`parcial.crear` por cada peso, `llenado.cerrar`).
 *
 * Mismo armazón que verde: un paso por pantalla, borrador persistido en cada
 * cambio, revisión final tocable y confirmación manteniendo apretado. El
 * «¿quién?» no se pregunta en un teléfono personal —viene contestado por el
 * enrolamiento y no se cambia: si hoy carga otra, lo hace desde su teléfono o
 * desde el muleto, que sí pregunta (10/9: el teléfono no se presta)—.
 */

import { BorradorYaConfirmado, encolar, historiaDeProduccion, mutacionesDeAvisos, mutacionesDeProduccion, type MutacionAEncolar } from '../datos/cola';
import { escribirConfig } from '../datos/bd';
import type { AvisoVisible } from '../nucleo/avisos';
import { cuantasNotas, listaDeNotas, notaParaLaCola } from './notas';
import { comprimirFoto, guardarFotoDelBorrador, leerFoto, sha256HexDeBlob } from '../datos/fotos';
import { generarUlid } from '../../src/ulid';
import type { Entidad, FotoLocal } from '../datos/bd';
import {
	anteriorDe,
	armarPayloadAbrir,
	armarPayloadAviso,
	armarPayloadCarro,
	armarPayloadCerrar,
	armarPayloadDescartar,
	armarPayloadParada,
	armarPayloadParcial,
	armarPayloadParcialDelCierre,
	AVISO_BRUTO_KG,
	chocaLaApertura,
	cierrePosteriorALaApertura,
	cierrePosteriorAlUltimoParcial,
	cierraVacio,
	dejaParcial,
	desdeDe,
	evaluarPesada,
	finPosteriorAlInicio,
	MAX_BRUTO_KG,
	MAX_DIGITOS_PRECINTO,
	MAX_MINUTOS_PARADA,
	MAX_TEXTO_AVISO,
	MIN_DIGITOS_PRECINTO,
	minutosValidos,
	precintoCompleto,
	taraAceptable,
	taraConRestos,
	textoValido,
	carroElegible,
	loteDelUltimoCarro,
	mezclasParaElegir,
	catalogoDeOtraJornada,
	viajeDelCarro,
	type BorradorAbrir,
	type BorradorAviso,
	type BorradorCarro,
	type BorradorCerrar,
	type BorradorDescartar,
	type BorradorNota,
	type BorradorParada,
	type BorradorProduccion,
	type BorradorTurno,
	type Pesada,
	type PesadaDeSilo,
	type SiloAbierto,
} from '../nucleo/produccion';
import {
	cuandoCorto,
	fechaDeProduccion,
	horaCorta,
	inicioDelTurno,
	instanteLocal,
	msDeInstanteLocal,
	HORARIO_TURNO,
	NOMBRE_TURNO,
	TURNOS,
	turnoDe,
	type Turno,
} from '../nucleo/tiempo';
import { botonMantener, colorDeNumero, el, flag, icono, kg, mil, padAnclado, PALETA, teclado, tile } from './componentes';
import {
	acciones,
	ahoraMs,
	app,
	campaniaAbiertaId,
	conPersonaHabitual,
	descartarBorrador,
	guardarBorrador,
	irA,
	nuevoBorradorDescartar,
	numeroDeSilo,
	recalcularAvisos,
	recalcularSilos,
	silosAbiertos,
} from './estado';
import { grillaPersonas, nombreDe, pregunta, reloj } from './pantallas-captura';
import { queMotivo } from './pantallas-sistema';

/** Los pasos de cada flujo, en orden. El cambio de turno es dinámico: ver `pasosDe`. */
const PASOS = {
	abrir: ['quien', 'silo', 'lote', 'trieur', 'horno', 'precinto', 'foto', 'tara', 'hora', 'revisar'],
	turno: ['quien', 'turno', 'bruto', 'hasta', 'recibe', 'revisar'],
	// v6 (§12.4): «¿Alguna nota?» antes de revisar, opcional.
	cerrar: ['quien', 'abierto', 'bruto', 'hora', 'nota', 'revisar'],
	descartar: ['quien', 'abierto', 'motivo', 'hora', 'revisar'],
	carro: ['quien', 'carro', 'hora', 'revisar'],
	parada: ['quien', 'motivo', 'detalle', 'minutos', 'turno', 'revisar'],
	// v6 (§12.2, §12.4): un aviso para las demás encargadas y una nota a un silo abierto.
	aviso: ['quien', 'texto', 'revisar'],
	nota: ['quien', 'silo_nota', 'texto', 'revisar'],
} as const satisfies Record<BorradorProduccion['tipo'], readonly string[]>;

/**
 * Los pasos de ESTE borrador. En el cambio de turno hay una pantalla de peso por
 * silo abierto (`bruto:0`, `bruto:1`): con dos silos son dos pesadas de corrido.
 */
export function pasosDe(b: BorradorProduccion): string[] {
	if (b.tipo !== 'turno') return [...PASOS[b.tipo]];
	// v6 (§12.2): «¿Algo para la que entra?» antes de revisar, opcional.
	return ['quien', 'turno', ...b.silos.map((_, i) => `bruto:${i}`), 'hasta', 'recibe', 'aviso', 'revisar'];
}

/** Contaminación grave: el silo entero se tira (r. 62). Catálogo corto del boceto. */
const MOTIVOS_DESCARTE = ['aceite hidráulico', 'restos de pintura', 'otro'];

const comprometiendo = new Set<string>();

function hornosActivos() {
	return (app.catalogos?.hornos ?? []).filter((h) => h.activo === 1);
}

/**
 * Los pasos por los que se navega hacia adelante. Los salteados siguen siendo
 * alcanzables tocando su fila en la revisión: saltear no es esconder.
 */
export function indicesDe(b: BorradorProduccion): number[] {
	const pasos = pasosDe(b);
	const salteados = new Set<string>();
	// Teléfono personal: el «¿quién?» ya está contestado (§5.4).
	if (b.personas.length > 0) salteados.add('quien');
	// Se entró tocando la tarjeta del silo en el inicio: ya está elegido.
	if (b.tipo === 'cerrar' && b.desde_inicio && b.llenado_id !== null) salteados.add('abierto');
	// La nota a un silo se empezó tocando el silo: ya está elegido.
	if (b.tipo === 'nota' && b.llenado_id !== null) salteados.add('silo_nota');
	// El horno se pregunta solo si hay más de uno activo («el azul aparece cuando
	// gestión lo active»); con uno solo, el borrador ya nace con él. Se saltea solo
	// si QUEDÓ resuelto: si el catálogo cambió con el borrador a medias, se pregunta,
	// porque el payload lo exige igual (Codex, ronda del diff, 8/9).
	if (b.tipo === 'abrir' && b.horno_id !== null && hornosActivos().length <= 1) salteados.add('horno');
	// El descarte propone «ahora» como hora y la muestra en la revisión: el paso existe
	// para corregirla desde «cambiar», no en el camino de ida (decisión del 8/9: sin
	// agregarle un paso al flujo; la fila faltaba —segunda pasada, P11, 12/9—).
	if (b.tipo === 'descartar') salteados.add('hora');
	return pasos.map((_, i) => i).filter((i) => !salteados.has(pasos[i]));
}

/** Deja el borrador parado en su primer paso visible: lo que main usa al arrancar un flujo. */
export function arrancarEn(b: BorradorProduccion): void {
	b.paso = indicesDe(b)[0] ?? 0;
}

function nombrePaso(b: BorradorProduccion): string {
	return pasosDe(b)[b.paso] ?? 'revisar';
}

async function irAlPaso(b: BorradorProduccion, paso: number): Promise<void> {
	b.paso = paso;
	await guardarBorrador(b).catch((error) => console.error('no se pudo guardar el borrador', error));
	app.render();
}

/**
 * El paso siguiente se busca por POSICIÓN y no por índice en la lista de
 * visibles: desde la revisión se puede saltar a un paso salteado (cambiar quién
 * en un muleto, por ejemplo), y desde ahí «Siguiente» tiene que seguir hacia
 * adelante en vez de volver al principio.
 */
async function avanzar(b: BorradorProduccion): Promise<void> {
	const siguiente = indicesDe(b).find((i) => i > b.paso);
	if (siguiente !== undefined) await irAlPaso(b, siguiente);
}

async function retroceder(b: BorradorProduccion): Promise<void> {
	const visibles = indicesDe(b);
	const anterior = [...visibles].reverse().find((i) => i < b.paso);
	if (anterior === undefined) {
		// Desde la primera pantalla, volver es volver al inicio: el borrador recién
		// nace y no se pierde nada.
		await descartarBorrador(b).catch(() => null);
		acciones.inicioProduccion();
		return;
	}
	await irAlPaso(b, anterior);
}

function pie(pieNodo: HTMLElement, b: BorradorProduccion, derecho?: { texto: string; deshabilitado?: boolean; alTocar: () => void }): void {
	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => void retroceder(b);
	pieNodo.appendChild(volver);
	if (derecho) {
		const boton = el('button', { class: 'btn go', type: 'button' }, derecho.texto);
		if (derecho.deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = derecho.alTocar;
		pieNodo.appendChild(boton);
	}
}

/** El lector grande de un número tipeado, con sus renglones de contexto. */
function lector(cuerpo: HTMLElement, valor: number | null, unidad: string, notas: Array<[string, string]> = []): void {
	const caja = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', valor === null ? { class: 'apagado' } : undefined, valor === null ? '0' : mil(valor)));
	grande.appendChild(el('span', { class: 'unit' }, unidad));
	caja.appendChild(grande);
	for (const [etiqueta, texto] of notas) {
		const fila = el('div', { class: 'barlab' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, texto));
		caja.appendChild(fila);
	}
	cuerpo.appendChild(caja);
}

/** Teclado de dígitos sobre un campo de texto del borrador (precinto). */
function tecladoTexto(b: BorradorProduccion, leer: () => string, escribir: (v: string) => void, maximo: number): HTMLElement {
	return teclado(
		(digito) => {
			const actual = leer();
			if (actual.length >= maximo) return;
			escribir(`${actual}${digito}`);
			void guardarBorrador(b);
			app.render();
		},
		() => {
			escribir(leer().slice(0, -1));
			void guardarBorrador(b);
			app.render();
		},
	);
}

/** Teclado de dígitos sobre un campo numérico del borrador. */
function tecladoNumero(b: BorradorProduccion, leer: () => number | null, escribir: (v: number | null) => void, digitos: number): HTMLElement {
	return teclado(
		(digito) => {
			const actual = String(leer() ?? '');
			if (actual.length >= digitos) return;
			escribir(Number(`${actual}${digito}`));
			void guardarBorrador(b);
			app.render();
		},
		() => {
			const actual = String(leer() ?? '');
			escribir(actual.length > 1 ? Number(actual.slice(0, -1)) : null);
			void guardarBorrador(b);
			app.render();
		},
	);
}

// ── El inicio de producción ───────────────────────────────────────────────

/**
 * Los silos abiertos arriba, como estado de la planta, y un botón por acción
 * (10/9). Tocar una tarjeta es cerrar ESE silo. Abrir va grande; cerrar, cambio
 * de turno, bajada de carro y parada en dos filas de dos.
 */
export function pintarInicioProduccion(cuerpo: HTMLElement, pieNodo: HTMLElement, aviso: string | null): void {
	const abiertos = silosAbiertos();
	// v6 (§12.2): los avisos entre encargadas, arriba de todo.
	franjaDeAvisos(cuerpo);
	cuerpo.appendChild(el('div', { class: 'q arriba' }, `Turno ${NOMBRE_TURNO[turnoDe(ahoraMs())]}`));
	cuerpo.appendChild(
		el(
			'div',
			{ class: 'qsub' },
			abiertos.length === 0 ? 'No hay silos abiertos.' : abiertos.length === 1 ? '1 silo abierto' : `${abiertos.length} silos abiertos`,
		),
	);

	const grilla = el('div', { class: 'grid1' });
	// La tarjeta lleva el verbo («cerrar ›»): es la misma tarjeta que en «¿qué silo se
	// llenó?», donde el toque solo elige, y nada más la distinguía (segunda pasada, 11/9).
	for (const silo of abiertos) grilla.appendChild(tarjetaSilo(silo, () => acciones.cerrarSilo(silo), 'cerrar ›'));
	cuerpo.appendChild(grilla);

	// Nunca hay más de dos silos abiertos a la vez (gerenta, 7/9): con dos, «Abrir»
	// directamente no está —ni apagado ni con cartel— y quedan los cuatro botones (10/9).
	if (abiertos.length < 2) {
		const abrir = el('button', { class: 'btn go ancho', type: 'button' }, 'Abrir un silo');
		abrir.onclick = () => acciones.abrirSilo();
		cuerpo.appendChild(abrir);
	}
	if (aviso) cuerpo.appendChild(flag('warn', aviso, ''));

	const sinSilos = abiertos.length === 0;
	const fila = el('div', { class: 'grid2 acciones' });
	const botones: Array<[string, string, boolean, () => void]> = [
		['Cerrar silo', 'se llenó', sinSilos, () => acciones.cerrarSilo()],
		// Sin silos no hay nada que registrar: el cambio de turno ES la pesada.
		['Cambio de turno', sinSilos ? 'no hay silos que pesar' : 'pesa los abiertos', sinSilos, () => acciones.cambioDeTurno()],
		['Bajada de carro', 'cuál bajó', false, () => acciones.cambioDeCarro()],
		['Parada', 'minutos y motivo', false, () => acciones.parada()],
		// v6 (§12.2, §12.4)
		['Dejar un aviso', 'para las que vienen', false, () => acciones.dejarAviso()],
		['Nota a un silo', sinSilos ? 'no hay silos abiertos' : 'la ven todas', sinSilos, () => acciones.notaASilo()],
	];
	for (const [texto, sub, deshabilitado, alTocar] of botones) {
		const boton = el('button', { class: 'btn accion', type: 'button' }, texto);
		boton.appendChild(el('small', undefined, sub));
		if (deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = alTocar;
		fila.appendChild(boton);
	}
	cuerpo.appendChild(fila);

	const ver = el('button', { class: 'btn ghost ancho', type: 'button' }, 'Ver las últimas 24 horas');
	ver.onclick = () => acciones.verLoCargado();
	pieNodo.appendChild(ver);
}

function nombreDeLote(loteId: string): string {
	return (app.catalogos?.lotes_campo ?? []).find((l) => l.id === loteId)?.nombre ?? '—';
}

function numeroDeTrieur(trieurId: string): string {
	const fila = (app.catalogos?.trieurs ?? []).find((t) => t.id === trieurId);
	return fila ? String(fila.numero) : '—';
}

function tarjetaSilo(silo: SiloAbierto, alTocar?: () => void, verbo?: string): HTMLElement {
	const caja = el(alTocar ? 'button' : 'div', { class: 'tile ancho', type: 'button' });
	const numero = el('div', { class: 'cuadro' }, numeroDeSilo(silo));
	numero.style.background = colorDeNumero(numeroDeSilo(silo));
	caja.appendChild(numero);
	const textos = el('span', undefined, `precinto ${silo.precinto}`);
	textos.appendChild(
		el(
			'small',
			undefined,
			// La última pesada, que el catálogo sí trae; el conteo de turnos solo se conoce
			// mientras las pesadas están en la cola, y la tarjeta no puede cambiar al subir.
			`${nombreDeLote(silo.lote_campo_id)} · trieur ${numeroDeTrieur(silo.trieur_id)} · ${
				silo.ultimo_fin_en === null ? `abierto ${cuandoCorto(silo.abierto_en, ahoraMs())}` : `pesado ${cuandoCorto(silo.ultimo_fin_en, ahoraMs())}`
			}${silo.local ? ' · sin subir' : ''}${cuantasNotas(silo.llenado_id)}`,
		),
	);
	caja.appendChild(textos);
	// El peso con nombre: el bruto grande —lo que marca la balanza— y el neto abajo.
	// Sin pesar, la tara, que es lo único que se sabe de él.
	const peso = el('span', { class: 'peso' });
	if (silo.ultimo_bruto_g === null) {
		peso.appendChild(el('b', undefined, 'sin pesar'));
		peso.appendChild(el('small', undefined, `tara ${kg(silo.tara_base_g)} kg`));
	} else {
		peso.appendChild(el('b', undefined, `${kg(silo.ultimo_bruto_g)} kg`));
		peso.appendChild(el('small', undefined, `bruto · ${kg(silo.ultimo_bruto_g - silo.tara_base_g)} kg netos`));
	}
	if (verbo) peso.appendChild(el('small', { class: 'verbo' }, verbo));
	caja.appendChild(peso);
	if (alTocar) (caja as HTMLButtonElement).onclick = alTocar;
	return caja;
}

// ── El flujo ──────────────────────────────────────────────────────────────

export function pintarProduccion(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion): void {
	const visibles = indicesDe(b);
	const puntos = el('div', { class: 'dots' });
	// Cuenta los visibles ya pasados: desde un paso fuera del camino (la hora del
	// descarte, entrada por «cambiar») los puntos siguen diciendo dónde se está.
	const hechos = visibles.filter((i) => i <= b.paso).length;
	for (let i = 0; i < visibles.length; i++) {
		const punto = el('i');
		if (i < hechos) punto.className = 'on';
		puntos.appendChild(punto);
	}
	cuerpo.appendChild(puntos);
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);

	const nombre = nombrePaso(b);
	// Las pesadas del cambio de turno: `bruto:N` es el silo N de la lista.
	if (b.tipo === 'turno' && nombre.startsWith('bruto:')) {
		const pesada = b.silos[Number(nombre.slice('bruto:'.length))];
		if (pesada) return pBruto(contenido, pieNodo, b, pesada, false);
	}
	switch (nombre) {
		case 'quien':
			return pQuien(contenido, pieNodo, b);
		case 'silo':
			return pSilo(contenido, pieNodo, b as BorradorAbrir);
		case 'lote':
			return pLote(contenido, pieNodo, b as BorradorAbrir);
		case 'trieur':
			return pTrieur(contenido, pieNodo, b as BorradorAbrir);
		case 'horno':
			return pHorno(contenido, pieNodo, b as BorradorAbrir);
		case 'precinto':
			return pPrecinto(contenido, pieNodo, b as BorradorAbrir);
		case 'foto':
			return pFoto(contenido, pieNodo, b as BorradorAbrir);
		case 'tara':
			return pTara(contenido, pieNodo, b as BorradorAbrir);
		case 'abierto':
			return pAbierto(contenido, pieNodo, b);
		case 'turno':
			return pTurno(contenido, pieNodo, b);
		case 'bruto':
			return pBruto(contenido, pieNodo, b, b as BorradorCerrar, true);
		case 'hasta':
			return pHasta(contenido, pieNodo, b as BorradorTurno);
		case 'recibe':
			return pRecibe(contenido, pieNodo, b as BorradorTurno);
		case 'motivo':
			return b.tipo === 'descartar' ? pMotivoDescarte(contenido, pieNodo, b) : pMotivoParada(contenido, pieNodo, b as BorradorParada);
		case 'detalle':
			return pDetalle(contenido, pieNodo, b as BorradorParada);
		case 'minutos':
			return pMinutos(contenido, pieNodo, b as BorradorParada);
		case 'carro':
			return pCarro(contenido, pieNodo, b as BorradorCarro);
		case 'texto':
			return pTexto(contenido, pieNodo, b as BorradorAviso | BorradorNota);
		case 'silo_nota':
			return pSiloNota(contenido, pieNodo, b as BorradorNota);
		case 'aviso':
			return pAvisoDelTurno(contenido, pieNodo, b as BorradorTurno);
		case 'nota':
			return pNotaDelCierre(contenido, pieNodo, b as BorradorCerrar);
		case 'hora':
			return pHora(contenido, pieNodo, b);
		default:
			return pRevisar(contenido, pieNodo, b);
	}
}

// ── Las pantallas ─────────────────────────────────────────────────────────

function pQuien(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion): void {
	const titulo =
		b.tipo === 'turno'
			? '¿Quién entrega el turno?'
			: b.tipo === 'cerrar'
				? '¿Quién cierra el silo?'
				: b.tipo === 'descartar'
					? '¿Quién lo descarta?'
					: b.tipo === 'aviso'
						? '¿Quién deja el aviso?'
						: b.tipo === 'nota'
							? '¿Quién escribe la nota?'
							: '¿Quién sos?';
	// Acá se llega solo si el teléfono no tiene dueña —el muleto— o si la dieron de
	// baja: en un teléfono personal el «quién» viene del enrolamiento y no se toca.
	pregunta(cuerpo, titulo, 'Tocá tu nombre.');
	cuerpo.appendChild(
		grillaPersonas(
			b.personas,
			1,
			() => {
				void guardarBorrador(b);
				app.render();
			},
			'produccion',
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.personas.length === 0, alTocar: () => void avanzar(b) });
}

function pSilo(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	pregunta(cuerpo, '¿Qué silo abrís?', 'Los ocupados están apagados: ya tienen un llenado abierto.');
	const ocupados = new Set(silosAbiertos().map((s) => s.silo_fisico_id));
	const grilla = el('div', { class: 'grid4' });
	for (const silo of (app.catalogos?.silos ?? []).filter((s) => s.activo === 1).sort((a, c) => a.numero - c.numero)) {
		const ocupado = ocupados.has(silo.id);
		grilla.appendChild(
			tile({
				// El número grande en el cuadro de color, como los carros de verde; la etiqueta
				// chica era lo único que decía cuál era (segunda pasada, 11/9).
				etiqueta: '',
				sub: ocupado ? 'en uso' : undefined,
				color: PALETA[silo.numero % PALETA.length],
				avatarCuadrado: true,
				avatarTexto: String(silo.numero),
				chico: true,
				seleccionado: b.silo_fisico_id === silo.id,
				deshabilitado: ocupado,
				alTocar: () => {
					b.silo_fisico_id = silo.id;
					void guardarBorrador(b);
					app.render();
				},
			}),
		);
	}
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.silo_fisico_id === null, alTocar: () => void avanzar(b) });
}

/** Un lote de «¿De qué lote es?», con lo que dice su tarjeta. */
export interface OpcionDeLote {
	lote: { id: string; nombre: string };
	badge?: string;
	sub?: string;
}

/**
 * Lo que muestra «¿De qué lote es?» al abrir un silo (§12.1, issue #82; §12.16 para las mezclas).
 *
 * Primero las mezclas de blendeo de la campaña, en curso o terminadas hace menos de tres jornadas
 * (`mezclasParaElegir`), con la etiqueta «mezcla»; las terminadas antes, aparte, en `viejas` («Otras
 * mezclas de la campaña»). Después los lotes del campo. Si se sabe el lote del último carro bajado
 * (`loteDelUltimoCarro`) y está en la lista, va primero entre los del campo con el carro de badge
 * («carro 2») y debajo a qué hora bajó, y viene marcado si el borrador todavía no tiene lote —la
 * pantalla no se saltea: Nicolás, 7/10—. Pero la línea alterna verde y blendeo (Agata, 9/10), y un lote
 * de verde marcado de más mientras la línea blendea contaría el silo como producción primaria: **con
 * mezclas en la lista, o con un catálogo de otra jornada, no se marca nada solo**, y una propuesta
 * automática anterior se borra; lo que eligió la encargada se respeta. Un lote del borrador que ya no
 * está en la lista (dado de baja, de otra campaña) se borra y hay que elegir de nuevo.
 */
export function pasoDelLote(b: BorradorAbrir): { subtitulo: string; opciones: OpcionDeLote[]; viejas: OpcionDeLote[] } {
	const ultimo = app.preferencias.ultimo_lote_id;
	const hora = app.preferencias.catalogos_hora_servidor;
	const catalogo = app.catalogos?.lotes_campo ?? [];
	const delCampo = catalogo.filter((l) => l.activo === 1 && l.tipo !== 'blendeo');
	const mezclas = mezclasParaElegir(catalogo, b.campania_id ?? campaniaAbiertaId(), hora);
	const hayMezclas = mezclas.recientes.length > 0;
	const delCarro = app.catalogos ? loteDelUltimoCarro(app.catalogos, app.bajadasLocales, hora) : null;
	// Un lote dado de baja (o que este catálogo no tiene) no se propone.
	const propuesta = delCarro !== null && delCampo.some((l) => l.id === delCarro.lote_campo_id) ? delCarro : null;
	const enLaLista = [...delCampo, ...mezclas.recientes, ...mezclas.viejas];
	// Lo elegido que ya no está (una mezcla que se dio de baja, otra campaña) se borra, y hasta que la encargada
	// toque otra opción no se propone nada: si no, se la reemplazaba en el acto por el lote de verde (Codex, ronda
	// del diff del 9/10).
	if (b.lote_campo_id !== null && !enLaLista.some((l) => l.id === b.lote_campo_id)) {
		b.lote_campo_id = null;
		b.lote_propuesto = false;
		b.elegir_de_nuevo = true;
	}
	const sinMarcar = hayMezclas || catalogoDeOtraJornada(hora, ahoraMs());
	// Una propuesta automática no sobrevive a que haya mezclas o a un catálogo de otra jornada. Tampoco el lote de
	// un borrador de antes de §12.16, que no dice si lo puso la propuesta o la encargada (`lote_propuesto` ausente):
	// ante la duda, que elija (Codex, ronda del diff del 9/10). Lo que eligió ella (`false`) se respeta.
	if (sinMarcar && b.lote_campo_id !== null && b.lote_propuesto !== false) {
		b.lote_campo_id = null;
		b.lote_propuesto = false;
	}
	const marcar = propuesta !== null && !sinMarcar && b.elegir_de_nuevo !== true;
	if (marcar && propuesta !== null && b.lote_campo_id === null) {
		b.lote_campo_id = propuesta.lote_campo_id;
		b.lote_propuesto = true;
	}
	const deMezcla = (m: (typeof mezclas.recientes)[number]): OpcionDeLote => ({
		lote: m,
		badge: 'mezcla',
		sub: m.cerrado_en === null || m.cerrado_en === undefined ? 'en curso' : `terminó ${cuandoCorto(instanteLocal(Date.parse(m.cerrado_en)), ahoraMs())}`,
	});
	const rango = (id: string): number => (id === propuesta?.lote_campo_id ? 2 : id === ultimo ? 1 : 0);
	const delCampoOrdenados = delCampo
		.sort((a, c) => rango(c.id) - rango(a.id) || a.nombre.localeCompare(c.nombre))
		.map((lote): OpcionDeLote => {
			if (propuesta !== null && lote.id === propuesta.lote_campo_id) {
				const carro = (app.catalogos?.carros ?? []).find((c) => c.id === propuesta.carro_id);
				return { lote, badge: `carro ${carro === undefined ? '—' : String(carro.numero)}`, sub: `bajó ${cuandoCorto(propuesta.registrado_en, ahoraMs())}` };
			}
			return { lote, badge: lote.id === ultimo ? 'el último' : undefined };
		});
	const subtitulo = hayMezclas
		? 'La línea puede estar con verde o con una mezcla: elegí de dónde viene.'
		: propuesta === null
			? 'El último usado, primero.'
			: 'El del último carro que bajó, primero.';
	return { subtitulo, opciones: [...mezclas.recientes.map(deMezcla), ...delCampoOrdenados], viejas: mezclas.viejas.map(deMezcla) };
}

function pLote(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	const paso = pasoDelLote(b);
	pregunta(cuerpo, '¿De qué lote es?', paso.subtitulo);
	const elegir = (lote: { id: string }) => {
		b.lote_campo_id = lote.id;
		b.lote_propuesto = false;
		b.elegir_de_nuevo = false;
		void guardarBorrador(b);
		app.render();
	};
	const grillaDe = (opciones: OpcionDeLote[], desde: number): HTMLElement => {
		const grilla = el('div', { class: 'grid2' });
		opciones.forEach(({ lote, badge, sub }, indice) => {
			grilla.appendChild(
				tile({
					etiqueta: lote.nombre,
					sub,
					badge,
					color: PALETA[(desde + indice) % PALETA.length],
					avatarIcono: 'lote',
					seleccionado: b.lote_campo_id === lote.id,
					alTocar: () => elegir(lote),
				}),
			);
		});
		return grilla;
	};
	cuerpo.appendChild(grillaDe(paso.opciones, 0));
	// Las mezclas terminadas hace más de tres jornadas, plegadas: un silo puede salir tarde con lo que quedó en la línea.
	if (paso.viejas.length > 0) {
		const elegidaVieja = paso.viejas.some(({ lote }) => lote.id === b.lote_campo_id);
		if (b.ver_mezclas_terminadas === true || elegidaVieja) {
			const viejas = grillaDe(paso.viejas, paso.opciones.length);
			viejas.style.marginTop = '10px';
			cuerpo.appendChild(viejas);
		} else {
			const ver = el('button', { class: 'enlace', type: 'button' }, `Otras mezclas de la campaña (${String(paso.viejas.length)})`);
			ver.addEventListener('click', () => {
				b.ver_mezclas_terminadas = true;
				void guardarBorrador(b);
				app.render();
			});
			cuerpo.appendChild(ver);
		}
	}
	// Seguir con la propuesta marcada es confirmarla: la pantalla no se saltea (Nicolás, 7/10). Desde acá el lote es de
	// la encargada, y que después aparezcan mezclas no lo borra (Codex, ronda del diff del 9/10, pedía revalidar al
	// encolar: se resolvió así, para no devolverla a esta pantalla desde la revisión).
	pie(pieNodo, b, {
		texto: 'Siguiente',
		deshabilitado: b.lote_campo_id === null,
		alTocar: () => {
			b.lote_propuesto = false;
			void avanzar(b);
		},
	});
}

function pTrieur(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	const hornos = hornosActivos();
	// Un silo abierto por trieur (Nicolás, 12/9): el trieur que ya tiene uno está apagado,
	// como los silos ocupados. Se mira contra la proyección de abiertos, que incluye lo
	// que este teléfono abrió y todavía no subió.
	const ocupados = new Map(silosAbiertos().map((s) => [s.trieur_id, s]));
	pregunta(
		cuerpo,
		'¿Por qué trieur entra?',
		ocupados.size > 0
			? 'El apagado ya tiene un silo abierto: se cierra ese antes de abrir otro en el mismo trieur.'
			: hornos.length === 1
				? `Horno ${hornos[0].nombre}. El otro aparece cuando gestión lo active.`
				: 'El silo sí sabe a cuál pertenece.',
	);
	const grilla = el('div', { class: 'grid2' });
	for (const trieur of (app.catalogos?.trieurs ?? []).filter((t) => t.activo === 1).sort((a, c) => a.numero - c.numero)) {
		const ocupadoPor = ocupados.get(trieur.id);
		grilla.appendChild(
			tile({
				etiqueta: `T${trieur.numero}`,
				sub: ocupadoPor ? `en uso · silo ${numeroDeSilo(ocupadoPor)}` : `trieur ${trieur.numero}`,
				color: PALETA[trieur.numero % PALETA.length],
				seleccionado: b.trieur_id === trieur.id,
				deshabilitado: ocupadoPor !== undefined,
				alTocar: () => {
					b.trieur_id = trieur.id;
					void guardarBorrador(b);
					app.render();
				},
			}),
		);
	}
	cuerpo.appendChild(grilla);
	// Un borrador viejo puede traer un trieur que se ocupó mientras tanto: tampoco pasa.
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.trieur_id === null || ocupados.has(b.trieur_id), alTocar: () => void avanzar(b) });
}

function pHorno(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	pregunta(cuerpo, '¿Por qué horno entró?', 'Hay más de uno activo.');
	const grilla = el('div', { class: 'grid2' });
	hornosActivos().forEach((horno, indice) => {
		grilla.appendChild(
			tile({
				etiqueta: horno.nombre,
				color: PALETA[indice % PALETA.length],
				seleccionado: b.horno_id === horno.id,
				alTocar: () => {
					b.horno_id = horno.id;
					void guardarBorrador(b);
					app.render();
				},
			}),
		);
	});
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.horno_id === null, alTocar: () => void avanzar(b) });
}

function pPrecinto(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	pregunta(arriba, '¿Qué precinto le ponés?', `El número grabado en el sello: de ${MIN_DIGITOS_PRECINTO} a ${MAX_DIGITOS_PRECINTO} dígitos.`);
	const casillas = el('div', { class: 'casillas' });
	for (let i = 0; i < MAX_DIGITOS_PRECINTO; i++) {
		const clase = `casilla${i >= MIN_DIGITOS_PRECINTO ? ' opcional' : ''}${i === b.precinto.length ? ' activa' : ''}`;
		casillas.appendChild(el('div', { class: clase }, b.precinto[i] ?? (i >= MIN_DIGITOS_PRECINTO ? '·' : '')));
	}
	arriba.appendChild(casillas);

	// El precinto es único por campaña y uno cerrado sigue reservado: se mira contra
	// los silos abiertos que este teléfono conoce Y contra la lista de usados que baja
	// el servidor (§5.5). Atajarlo acá evita un `precinto_repetido` que aparecería
	// recién al sincronizar, con el silo ya cargándose.
	const enUso = silosAbiertos().find((s) => s.precinto === b.precinto);
	const yaUsado = enUso !== undefined || (app.catalogos?.precintos_usados ?? []).includes(b.precinto);
	// El cartel va en la franja del medio, nunca arriba del pad: al completar un
	// precinto repetido aparecía encima del teclado y lo bajaba bajo el dedo (11/9).
	if (yaUsado) {
		medio.appendChild(
			flag(
				'stop',
				enUso ? `Ese precinto ya está en el silo ${numeroDeSilo(enUso)}.` : 'Ese precinto ya se usó en esta campaña.',
				'Fijate el número del sello.',
			),
		);
	}
	cuerpo.appendChild(
		tecladoTexto(
			b,
			() => b.precinto,
			(v) => {
				b.precinto = v;
			},
			MAX_DIGITOS_PRECINTO,
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !precintoCompleto(b.precinto) || yaUsado, alTocar: () => void avanzar(b) });
}

/** Blobs de las fotos sacadas en esta sesión de pantalla, para la miniatura sin releer IndexedDB. */
const blobsEnMemoria = new Map<string, Blob>();
const releyendo = new Set<string>();
/** El error de la última captura, atado al borrador que lo produjo: no contamina al siguiente. */
let avisoFoto: { capture_id: string; texto: string } | null = null;

/**
 * La foto del precinto (§5.3; Agata, 14/9: «la agregamos ahora»). El blob y el borrador
 * se guardan en UNA transacción, solo si el borrador sigue vivo; la foto sube sola después
 * de que el silo tenga acuse. «Seguir sin foto» queda como escape: una cámara rota no puede
 * impedir abrir el silo.
 */
function pFoto(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	const foto = b.foto ?? null;
	// La caché de miniaturas no crece: solo la foto de este borrador.
	for (const clave of [...blobsEnMemoria.keys()]) if (clave !== foto?.foto_id) blobsEnMemoria.delete(clave);
	pregunta(cuerpo, 'Sacale una foto al precinto', 'Que se lea el número: es la evidencia de que quedó bien cargado.');
	if (foto !== null) {
		const blob = blobsEnMemoria.get(foto.foto_id);
		if (blob) {
			const imagen = el('img', { class: 'miniatura', alt: 'Foto del precinto' }) as HTMLImageElement;
			const url = URL.createObjectURL(blob);
			imagen.src = url;
			imagen.onload = () => URL.revokeObjectURL(url);
			imagen.onerror = () => URL.revokeObjectURL(url);
			cuerpo.appendChild(imagen);
		} else {
			cuerpo.appendChild(flag('ok', 'Foto guardada.', 'Sacá otra si no se lee.'));
			// Tras recargar, el blob se relee una vez para la miniatura. Si ya no está (otra pestaña
			// lo quitó), el borrador deja de declararla y «Siguiente» se traba hasta sacar otra.
			if (!releyendo.has(foto.foto_id)) {
				releyendo.add(foto.foto_id);
				void leerFoto(app.bd, foto.foto_id)
					.then(async (fila) => {
						if (fila) {
							blobsEnMemoria.set(foto.foto_id, fila.blob);
						} else if (b.foto?.foto_id === foto.foto_id) {
							b.foto = null;
							b.actualizado_en = Date.now();
							await guardarBorrador(b);
						}
						app.render();
					})
					.catch((error) => {
						console.error('no se pudo releer la foto', error);
						releyendo.delete(foto.foto_id);
					});
			}
		}
	}
	if (avisoFoto !== null && avisoFoto.capture_id === b.capture_id) cuerpo.appendChild(flag('stop', avisoFoto.texto, ''));

	const entrada = el('input', { type: 'file', accept: 'image/*', capture: 'environment', hidden: '' }) as HTMLInputElement;
	entrada.onchange = async () => {
		const archivo = entrada.files?.[0];
		if (!archivo) return;
		try {
			const blob = await comprimirFoto(archivo);
			const nueva = { foto_id: generarUlid(), capturada_en: new Date().toISOString() };
			const fila: FotoLocal = { ...nueva, entidad: 'llenado', entidad_id: b.llenado_id, mutacion_id: b.mutacion_id, blob, sha256: await sha256HexDeBlob(blob), confirmada: false };
			const anterior = b.foto ?? null;
			b.foto = nueva;
			b.sin_foto = false;
			b.actualizado_en = Date.now();
			// Solo si el borrador sigue en la base: una compresión que termina después de confirmar
			// o descartar no lo resucita ni le pisa la foto (Codex, ronda del diff, 14/9).
			if (!(await guardarFotoDelBorrador(app.bd, fila, b))) {
				b.foto = anterior;
				return;
			}
			if (anterior !== null) blobsEnMemoria.delete(anterior.foto_id);
			blobsEnMemoria.set(nueva.foto_id, blob);
			avisoFoto = null;
		} catch (error) {
			console.error('no se pudo guardar la foto', error);
			b.foto = foto;
			avisoFoto = { capture_id: b.capture_id, texto: 'No se pudo guardar la foto. Probá de nuevo o seguí sin foto.' };
		}
		app.render();
	};
	cuerpo.appendChild(entrada);

	const sacar = el('button', { class: 'opt grande', type: 'button' });
	const avatar = el('div', { class: 'av' });
	avatar.style.background = '#1B7A3C';
	avatar.appendChild(icono('camara', 22));
	sacar.appendChild(avatar);
	sacar.appendChild(el('span', undefined, foto !== null ? 'Sacar otra' : 'Sacar la foto'));
	sacar.onclick = () => entrada.click();
	cuerpo.appendChild(sacar);
	if (foto === null) {
		const seguir = el('button', { class: 'enlace', type: 'button' }, 'Seguir sin foto');
		seguir.onclick = () => {
			b.sin_foto = true;
			void avanzar(b);
		};
		cuerpo.appendChild(seguir);
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: foto === null, alTocar: () => void avanzar(b) });
}

function pTara(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAbrir): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	// El catálogo de silos no trae la tara: se lee de la balanza, cada vez. Es a propósito
	// (Agata, 14/9): pesarlo vacío es cómo se ve si quedaron restos del silo anterior.
	pregunta(arriba, '¿Cuánto pesa el silo vacío?', 'Pesalo vacío aunque la tarjeta ya lo diga: así se ve si quedaron restos del silo anterior.');
	lector(arriba, b.tara_kg, 'kg');
	// El cartel espera a que haya dos dígitos (con «6» tipeado era ruido); el pie está
	// trabado desde el primero y se destraba solo al completar. Fuera de rango no avisa:
	// bloquea (Nicolás, 12/9), porque las taras son siempre las mismas.
	const terminoDeTipear = b.tara_kg !== null && String(b.tara_kg).length >= 2;
	if (terminoDeTipear && !taraAceptable(b.tara_kg)) {
		medio.appendChild(flag('stop', 'Ese número no puede ser una tara.', 'Los silos vacíos pesan entre 60 y 70 kg. Fijate la tarjeta.'));
	} else if (terminoDeTipear && taraConRestos(b.tara_kg)) {
		medio.appendChild(flag('warn', 'Pesa más que un silo vacío.', 'Si quedaron restos del silo anterior, hay que vaciarlo: no se llena sobre material viejo.'));
	}
	cuerpo.appendChild(
		tecladoNumero(
			b,
			() => b.tara_kg,
			(v) => {
				b.tara_kg = v;
			},
			3,
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !taraAceptable(b.tara_kg), alTocar: () => void avanzar(b) });
}

function pAbierto(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion): void {
	if (b.tipo !== 'cerrar' && b.tipo !== 'descartar') return;
	pregunta(cuerpo, b.tipo === 'cerrar' ? '¿Qué silo se llenó?' : '¿Qué silo descartás?', 'Con lo último que se pesó.');
	const grilla = el('div', { class: 'grid1' });
	for (const silo of silosAbiertos()) {
		grilla.appendChild(tarjetaSilo(silo, () => elegirSilo(b, silo)));
	}
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.llenado_id === null, alTocar: () => void avanzar(b) });
}

/**
 * Elegir el silo CONGELA lo que se vio de él: la versión (base del CAS), el
 * último parcial (el testigo del cierre), el bruto anterior y el «desde». Un
 * refresco de catálogos a mitad de la carga no puede moverlos.
 */
export function elegirSilo(b: BorradorProduccion, silo: SiloAbierto): void {
	if (b.tipo !== 'cerrar' && b.tipo !== 'descartar') return;
	b.llenado_id = silo.llenado_id;
	b.silo_numero = numeroDeSilo(silo);
	b.precinto = silo.precinto;
	b.neto_visto_g = silo.ultimo_bruto_g === null ? null : silo.ultimo_bruto_g - silo.tara_base_g;
	b.intervalo = { silo_fisico_id: silo.silo_fisico_id, trieur_id: silo.trieur_id, abierto_en: silo.abierto_en, lote_campo_id: silo.lote_campo_id };
	b.version_base_id = silo.version_id;
	// Lo que hay que esperar para que la transición pueda viajar: el último parcial si
	// sigue en la cola, y si no, la apertura del silo cuando se abrió en este teléfono.
	b.padre_mutacion_id = silo.ultimo_parcial_mutacion_id ?? silo.abierto_mutacion_id;
	if (b.tipo === 'cerrar') {
		b.ultimo_parcial_id = silo.ultimo_parcial_id;
		// El parcial del cierre arranca donde terminó la última pesada: si esa pesada
		// sigue en la cola, depende de ella (mismo `padre_mutacion_id` que el cierre).
		b.abierto_en = silo.abierto_en;
		b.inicio_en = desdeDe(silo);
		b.anterior_g = anteriorDe(silo);
		b.tara_base_g = silo.tara_base_g;
		b.bruto_kg = null;
	}
	void guardarBorrador(b);
	app.render();
}

function pTurno(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion): void {
	if (b.tipo !== 'turno' && b.tipo !== 'parada') return;
	pregunta(cuerpo, `¿Qué turno ${b.tipo === 'parada' ? 'era' : 'entregás'}?`, 'Viene marcado el de ahora.');
	// El de ahora viene propuesto desde que nace el borrador (estado.ts): acá no se
	// escribe nada mientras se pinta.
	const grilla = el('div', { class: 'grid3' });
	for (const turno of TURNOS) {
		grilla.appendChild(
			tile({
				etiqueta: NOMBRE_TURNO[turno],
				sub: HORARIO_TURNO[turno],
				color: PALETA[TURNOS.indexOf(turno) % PALETA.length],
				seleccionado: b.turno === turno,
				alTocar: () => elegirTurno(b, turno),
			}),
		);
	}
	cuerpo.appendChild(grilla);
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Sale de la hora. Cambialo solo si hoy se cambiaron entre ustedes.'));
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.turno === null, alTocar: () => void avanzar(b) });
}

function elegirTurno(b: BorradorTurno | BorradorParada, turno: Turno): void {
	b.turno = turno;
	// La parada se fecha en el ARRANQUE del turno informado, no en la hora del
	// tipeo: a las 06:10 se anota la de la noche, que empezó a las 22:00 de ayer.
	if (b.tipo === 'parada') b.registrado_ms = inicioDelTurno(ahoraMs(), turno);
	void guardarBorrador(b);
	app.render();
}

/**
 * El peso de UN silo, en el cambio de turno o en el cierre. La regla cambia con
 * `cierre`: al entregar el turno el bruto tiene que superar al anterior (si no,
 * el parcial no dice nada); al cerrar puede ser el mismo, y entonces no viaja
 * ningún parcial.
 */
function pBruto(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion, pesada: Pesada & { silo_numero: string | null; precinto: string | null }, cierre: boolean): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	pregunta(arriba, `¿Cuánto pesa el silo ${pesada.silo_numero ?? ''} ahora?`, `Precinto ${pesada.precinto ?? '—'}. Con lo que tiene adentro, como marca la balanza.`);
	const anterior = pesada.anterior_g ?? 0;
	const tara = pesada.tara_base_g ?? 0;
	const brutoG = pesada.bruto_kg === null ? 0 : pesada.bruto_kg * 1000;
	// Cómo viene el silo: desde cuándo corre lo que se está pesando. El ordinal del
	// turno no se muestra porque solo se conoce mientras las pesadas están en la cola.
	const abierto = pesada.abierto_en === null ? null : cuandoCorto(pesada.abierto_en, ahoraMs());
	const ultima = pesada.inicio_en !== null && pesada.inicio_en !== pesada.abierto_en ? cuandoCorto(pesada.inicio_en, ahoraMs()) : null;
	lector(arriba, pesada.bruto_kg, 'kg', [
		[anterior > tara ? `− último pesado ${kg(anterior)} kg` : `− silo vacío ${kg(tara)} kg`, brutoG > anterior ? `${kg(brutoG - anterior)} kg este turno` : '—'],
		[cierre ? 'neto final del silo' : 'neto del silo hasta acá', brutoG > tara ? `${kg(brutoG - tara)} kg` : '—'],
		...(abierto === null ? [] : [[`abierto ${abierto}`, ultima === null ? 'sin pesar todavía' : `última pesada ${ultima}`] as [string, string]]),
	]);
	// Los carteles esperan a que lo tipeado tenga los dígitos de la referencia: con
	// «14» tipeado para 145 saltaba «no puede pesar menos que 140» (11/9).
	const digitosDeReferencia = Math.max(2, String(Math.round(anterior / 1000)).length);
	const terminoDeTipear = pesada.bruto_kg !== null && String(pesada.bruto_kg).length >= digitosDeReferencia;
	const veredicto = evaluarPesada(pesada.bruto_kg, anterior);
	const vacio = cierraVacio(cierre, pesada.bruto_kg, anterior, tara);
	if (terminoDeTipear) {
		if (!veredicto.ok && veredicto.motivo === 'excesivo') {
			medio.appendChild(flag('stop', `No puede pesar más de ${MAX_BRUTO_KG} kg.`, 'El ascensor no lo levanta. Fijate la balanza.'));
		} else if (!veredicto.ok) {
			medio.appendChild(
				flag('stop', `No puede pesar menos que ${anterior > tara ? 'lo último pesado' : 'vacío'}.`, `${kg(anterior)} kg. Fijate la balanza.`),
			);
		} else if (veredicto.aviso === 'muy_pesado') {
			medio.appendChild(flag('warn', `Más de ${AVISO_BRUTO_KG} kg.`, 'Envasado acepta hasta 170 con 20 de tolerancia. ¿Seguro?'));
		} else if (vacio) {
			// Cerrar vacío no se deja (Agata, 14/9): el silo abierto por error lo anula la oficina.
			medio.appendChild(flag('stop', 'Un silo no se cierra vacío.', 'Si tiene material, pesalo. Si se abrió por error, la oficina lo anula.'));
		} else if (!veredicto.pesa) {
			// Sin cambio: no hay turno que registrar para este silo (ni cierre que espere).
			medio.appendChild(
				cierre
					? flag('warn', 'El mismo peso que la última pesada.', 'Se cierra con ese neto.')
					: flag('warn', anterior > tara ? 'El mismo peso que la última pesada.' : 'Sigue vacío.', 'Este silo no registra turno: se sigue con el resto.'),
			);
		}
	}
	// Quien va a cerrar y ve aceite hidráulico no tiene por qué pesar el silo para
	// poder decir que se tira: el descarte se ofrece acá también (segunda pasada, 11/9).
	if (cierre && b.tipo === 'cerrar') {
		const tirar = el('button', { class: 'enlace', type: 'button' }, 'Está contaminado: descartar el silo');
		tirar.onclick = () => void pasarADescartar(b);
		medio.appendChild(tirar);
	}
	cuerpo.appendChild(
		tecladoNumero(
			b,
			() => pesada.bruto_kg,
			(v) => {
				pesada.bruto_kg = v;
			},
			3,
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !veredicto.ok || vacio, alTocar: () => void avanzar(b) });
}

function pHasta(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorTurno): void {
	// El «desde» de cada silo se ve en su pantalla de peso; acá solo el fin.
	pregunta(cuerpo, '¿Hasta qué hora va este turno?', 'Si terminás ahora, es ahora. Se calcula solo desde la última pesada de cada silo.');
	b.hasta_ms ??= ahoraMs();
	reloj(cuerpo, b.hasta_ms, 'hasta', (nuevo) => {
		b.hasta_ms = nuevo;
		void guardarBorrador(b);
		app.render();
	});
	// Cada parcial tiene su propio «desde»: el fin tiene que quedar después de todos
	// los que van a viajar (un silo sin cambio no deja parcial y no ata la hora).
	const tarde = b.silos.find((p) => dejaParcial(p) && !finPosteriorAlInicio(p.inicio_en, b.hasta_ms));
	if (tarde) {
		cuerpo.appendChild(
			flag('stop', 'Tiene que ser después del comienzo del turno.', `El silo ${tarde.silo_numero ?? '—'} viene desde ${(tarde.inicio_en ?? '').slice(11)}.`),
		);
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: tarde !== undefined, alTocar: () => void avanzar(b) });
}

function pRecibe(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorTurno): void {
	pregunta(cuerpo, '¿Quién recibe el turno?', 'Opcional: si la que entra no está, seguí.');
	const grilla = el('div', { class: 'grid2' });
	// Solo las de producción: el turno lo recibe otra encargada, no alguien de verde o del
	// laboratorio (11/9). Y quien entrega no puede recibir: el servidor lo rechaza y acá
	// directamente no se ofrece.
	const candidatas = (app.catalogos?.personas ?? []).filter((p) => p.activo === 1 && p.estacion_habitual === 'produccion' && p.id !== b.personas[0]);
	if (candidatas.length === 0) cuerpo.appendChild(el('div', { class: 'qsub' }, 'No hay otra encargada de producción en el catálogo.'));
	candidatas.forEach((persona, indice) => {
		grilla.appendChild(
			tile({
				etiqueta: persona.nombre,
				color: PALETA[indice % PALETA.length],
				avatarTexto: persona.nombre.slice(0, 1).toUpperCase(),
				seleccionado: b.recibe_id === persona.id,
				alTocar: () => {
					b.recibe_id = b.recibe_id === persona.id ? null : persona.id;
					void guardarBorrador(b);
					app.render();
				},
			}),
		);
	});
	cuerpo.appendChild(grilla);
	// Sin «Nadie todavía»: hacía lo mismo que «Siguiente» sin nadie elegido.
	pie(pieNodo, b, { texto: b.recibe_id === null ? 'Nadie todavía' : 'Siguiente', alTocar: () => void avanzar(b) });
}

function pMotivoDescarte(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorDescartar): void {
	pregunta(cuerpo, '¿Por qué se descarta?', 'Contaminación grave: el silo entero se tira.');
	const lista = el('div', { class: 'grid1' });
	for (const motivo of MOTIVOS_DESCARTE) {
		const opcion = el('button', { class: `opt${b.motivo === motivo ? ' sel' : ''}`, type: 'button' }, motivo);
		opcion.onclick = () => {
			b.motivo = motivo;
			void guardarBorrador(b);
			app.render();
		};
		lista.appendChild(opcion);
	}
	cuerpo.appendChild(lista);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.motivo === null, alTocar: () => void avanzar(b) });
}

function pMotivoParada(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorParada): void {
	pregunta(cuerpo, '¿Por qué paró?', 'Lo que más tiempo se llevó en 2025, primero.');
	const grilla = el('div', { class: 'grid2' });
	const motivos = (app.catalogos?.motivos_parada ?? []).filter((m) => m.activo === 1).sort((a, c) => a.orden - c.orden);
	motivos.forEach((motivo, indice) => {
		grilla.appendChild(
			tile({
				etiqueta: motivo.nombre,
				chico: true,
				color: PALETA[indice % PALETA.length],
				seleccionado: b.motivo_parada_id === motivo.id,
				alTocar: () => {
					b.motivo_parada_id = motivo.id;
					void guardarBorrador(b);
					app.render();
				},
			}),
		);
	});
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.motivo_parada_id === null, alTocar: () => void avanzar(b) });
}

function pDetalle(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorParada): void {
	// Pedido de Agata (7/9): «anotan 10 minutos por rotura y no tienen dónde decir
	// qué se rompió». Opcional y no vinculante: el motivo sigue siendo el botón.
	pregunta(cuerpo, '¿Qué pasó?', 'Opcional: qué se rompió, qué se atoró. Un renglón.');
	const texto = el('textarea', { class: 'nota', rows: '2', maxlength: '400', placeholder: 'Ej.: se cortó la cinta del trieur 2' });
	texto.value = b.nota ?? '';
	texto.oninput = () => {
		b.nota = texto.value;
		void guardarBorrador(b);
	};
	cuerpo.appendChild(texto);
	pie(pieNodo, b, { texto: (b.nota ?? '').trim() ? 'Siguiente' : 'Sin detalle', alTocar: () => void avanzar(b) });
}

function pMinutos(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorParada): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	pregunta(arriba, '¿Cuántos minutos?', 'A ojo, como se anota siempre.');
	lector(arriba, b.minutos, 'min');
	if (b.minutos !== null && b.minutos > MAX_MINUTOS_PARADA) {
		medio.appendChild(flag('stop', 'Más que un turno.', `Una parada no pasa de ${MAX_MINUTOS_PARADA} minutos.`));
	}
	cuerpo.appendChild(
		tecladoNumero(
			b,
			() => b.minutos,
			(v) => {
				b.minutos = v;
			},
			3,
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !minutosValidos(b.minutos), alTocar: () => void avanzar(b) });
}

/**
 * Qué dice el carro debajo y si se puede elegir (v6, §12.1; Nicolás, 6/10: solo los que figuran
 * traídos y sin bajar). El lote no se pregunta: sale del último viaje de verde que lo trajo. Un carro
 * sin viaje conocido dice «no figura traído» (verde todavía no lo subió, o no vino esta campaña) y
 * uno ya bajado desde esa llegada, «ya bajó · viaje 151»: los dos se ven apagados.
 */
export function estadoDelCarro(carroId: string): { sub: string | undefined; elegible: boolean } {
	const viaje = viajeDelCarro(carroId, app.catalogos?.carros_ultimo_viaje ?? [], app.bajadasLocales);
	const elegible = carroElegible(viaje);
	if (!viaje) return { sub: 'no figura traído', elegible };
	if (viaje.bajado) return { sub: viaje.nro_viaje === null ? 'ya bajó' : `ya bajó · viaje ${String(viaje.nro_viaje)}`, elegible };
	if (viaje.ambiguo) return { sub: 'no se sabe de qué viaje', elegible };
	if (viaje.nro_viaje === null) return { sub: undefined, elegible };
	const lote = viaje.lote_campo_id === null ? null : nombreDeLote(viaje.lote_campo_id);
	return { sub: `${lote ?? '—'} · viaje ${String(viaje.nro_viaje)}`, elegible };
}

function vieneDe(carroId: string): string | undefined {
	return estadoDelCarro(carroId).sub;
}

/** El carro del borrador, si todavía se puede elegir: el catálogo pudo cambiar desde que se tocó. */
function carroVigente(b: BorradorCarro): boolean {
	return b.carro_id !== null && estadoDelCarro(b.carro_id).elegible;
}

function pCarro(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorCarro): void {
	pregunta(cuerpo, '¿Qué carro bajó?', 'Solo los que figuran traídos y sin bajar. Debajo, de qué lote viene.');
	const grilla = el('div', { class: 'grid2' });
	let elegibles = 0;
	for (const carro of (app.catalogos?.carros ?? []).filter((c) => c.activo === 1).sort((a, c) => a.numero - c.numero)) {
		const estado = estadoDelCarro(carro.id);
		if (estado.elegible) elegibles += 1;
		grilla.appendChild(
			tile({
				etiqueta: '', // el número va en el cuadro de color; con la etiqueta decía «1 1»
				sub: estado.sub,
				color: carro.color ?? PALETA[carro.numero % PALETA.length],
				avatarCuadrado: true,
				avatarTexto: String(carro.numero),
				seleccionado: estado.elegible && b.carro_id === carro.id,
				// Apagado, como el silo ocupado en «¿Qué silo?»: se ve y dice por qué, pero no se toca.
				deshabilitado: !estado.elegible,
				alTocar: () => {
					b.carro_id = carro.id;
					void guardarBorrador(b);
					app.render();
				},
			}),
		);
	}
	cuerpo.appendChild(grilla);
	if (b.carro_id !== null && !carroVigente(b)) {
		// Se eligió y entre medio el catálogo lo dio por bajado (otro teléfono) o su viaje se anuló.
		const numero = (app.catalogos?.carros ?? []).find((c) => c.id === b.carro_id)?.numero ?? '—';
		cuerpo.appendChild(flag('warn', `El carro ${String(numero)} ya no se puede elegir.`, 'Figura bajado desde que se tocó acá, o su viaje se anuló. Tocá otro.'));
	} else if (elegibles === 0) {
		cuerpo.appendChild(flag('warn', 'Ningún carro figura traído sin bajar.', 'Si verde todavía no subió el viaje, el carro aparece cuando ese teléfono tenga señal.'));
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !carroVigente(b), alTocar: () => void avanzar(b) });
}

function pHora(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion): void {
	const titulo =
		b.tipo === 'cerrar' ? '¿A qué hora se cerró?' : b.tipo === 'descartar' ? '¿A qué hora se descartó?' : b.tipo === 'carro' ? '¿A qué hora bajó el carro?' : '¿A qué hora se abrió?';
	pregunta(cuerpo, titulo, 'Si fue recién, es ahora.');
	const leer = (): number => {
		if (b.tipo === 'abrir') return (b.abierto_ms ??= ahoraMs());
		if (b.tipo === 'cerrar' || b.tipo === 'descartar') return (b.cerrado_ms ??= ahoraMs());
		if (b.tipo === 'carro') return (b.registrado_ms ??= ahoraMs());
		return ahoraMs();
	};
	const actual = leer();
	reloj(cuerpo, actual, b.tipo === 'cerrar' ? 'hora del cierre' : b.tipo === 'descartar' ? 'hora del descarte' : 'hora', (nuevo) => {
		if (b.tipo === 'abrir') b.abierto_ms = nuevo;
		else if (b.tipo === 'cerrar' || b.tipo === 'descartar') b.cerrado_ms = nuevo;
		else if (b.tipo === 'carro') b.registrado_ms = nuevo;
		void guardarBorrador(b);
		app.render();
	});
	let ok = true;
	// Cerrar y descartar fechan el fin del silo: después de abrirlo y de su última pesada.
	if (b.tipo === 'cerrar' || b.tipo === 'descartar') {
		const silo = silosAbiertos().find((s) => s.llenado_id === b.llenado_id);
		const verbo = b.tipo === 'cerrar' ? 'cerrarse' : 'descartarse';
		if (!cierrePosteriorALaApertura(b.cerrado_ms, silo?.abierto_en ?? null)) {
			ok = false;
			cuerpo.appendChild(flag('stop', 'Tiene que ser después de la apertura.', `El silo se abrió ${(silo?.abierto_en ?? '').slice(11)}.`));
		} else if (b.tipo === 'cerrar' && dejaParcial(b) && !finPosteriorAlInicio(b.inicio_en, b.cerrado_ms)) {
			// El peso final es un parcial más, y un parcial no puede empezar y terminar
			// en el mismo minuto: el cierre tiene que quedar después de la última pesada.
			ok = false;
			cuerpo.appendChild(flag('stop', 'Tiene que ser después de la última pesada.', `La última terminó ${(b.inicio_en ?? '').slice(11)}.`));
		} else if (!cierrePosteriorAlUltimoParcial(b.cerrado_ms, silo?.ultimo_fin_en ?? null)) {
			ok = false;
			cuerpo.appendChild(
				flag('stop', `No puede ${verbo} antes de la última pesada.`, `La última terminó ${(silo?.ultimo_fin_en ?? '').slice(11)}.`),
			);
		}
	}
	if (b.tipo === 'abrir') {
		const aviso = avisoDeChoque(b);
		if (aviso) cuerpo.appendChild(aviso);
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !ok, alTocar: () => void avanzar(b) });
}

// ── v6: avisos entre encargadas y notas del silo (§12.2, §12.4) ───────────

/** Un texto libre de hasta 400 caracteres: el aviso o la nota. Obligatorio (sin texto no hay nada que mandar). */
function cajaDeTexto(cuerpo: HTMLElement, b: BorradorProduccion, leer: () => string | null | undefined, escribir: (v: string) => void, ejemplo: string): void {
	const texto = el('textarea', { class: 'nota', rows: '3', maxlength: String(MAX_TEXTO_AVISO), placeholder: ejemplo });
	texto.value = leer() ?? '';
	texto.oninput = () => {
		escribir(texto.value);
		void guardarBorrador(b);
	};
	cuerpo.appendChild(texto);
}

function pTexto(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAviso | BorradorNota): void {
	if (b.tipo === 'aviso') {
		pregunta(cuerpo, '¿Qué les querés avisar?', 'Lo ven las demás encargadas en su teléfono, arriba del inicio, durante un día.');
		cajaDeTexto(cuerpo, b, () => b.texto, (v) => (b.texto = v), 'Ej.: revisar el trieur 2, hace ruido');
	} else {
		pregunta(cuerpo, `Nota al silo ${b.silo ?? '—'}`, 'La ven producción, calidad y envasado cuando miran este precinto.');
		listaDeNotas(cuerpo, b.llenado_id, b.precinto_del_silo);
		cajaDeTexto(cuerpo, b, () => b.texto, (v) => (b.texto = v), 'Ej.: vino con tierra, mirar la humedad');
	}
	// El botón no se repinta en cada tecla: avanzar sin texto lo frena la revisión.
	pie(pieNodo, b, { texto: 'Siguiente', alTocar: () => void avanzar(b) });
}

function pSiloNota(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorNota): void {
	pregunta(cuerpo, '¿A qué silo?', 'Los silos abiertos.');
	const grilla = el('div', { class: 'grid1' });
	for (const silo of silosAbiertos()) {
		grilla.appendChild(
			tarjetaSilo(silo, () => {
				elegirSiloDeNota(b, silo);
				void avanzar(b);
			}),
		);
	}
	cuerpo.appendChild(grilla);
	pie(pieNodo, b);
}

/** Congela el silo en la nota: lo que la revisión y el payload dicen es lo que se eligió. */
export function elegirSiloDeNota(b: BorradorNota, silo: SiloAbierto): void {
	b.llenado_id = silo.llenado_id;
	b.silo = numeroDeSilo(silo);
	b.precinto_del_silo = silo.precinto;
	b.padre_mutacion_id = silo.abierto_mutacion_id;
	void guardarBorrador(b);
}

function pAvisoDelTurno(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorTurno): void {
	pregunta(cuerpo, '¿Algo para la que entra?', 'Opcional. Lo ve en su teléfono, arriba del inicio.');
	cajaDeTexto(cuerpo, b, () => b.aviso_texto, (v) => (b.aviso_texto = v), 'Ej.: revisar el trieur 2, hace ruido');
	pie(pieNodo, b, { texto: 'Siguiente', alTocar: () => void avanzar(b) });
}

function pNotaDelCierre(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorCerrar): void {
	pregunta(cuerpo, '¿Alguna nota del silo?', 'Opcional. La ven calidad y envasado cuando miran este precinto.');
	listaDeNotas(cuerpo, b.llenado_id, b.precinto);
	cajaDeTexto(cuerpo, b, () => b.nota_texto, (v) => (b.nota_texto = v), 'Ej.: el último tramo vino húmedo');
	pie(pieNodo, b, { texto: 'Siguiente', alTocar: () => void avanzar(b) });
}


/** Tocar «Anular» en el aviso propio lo deja armado; el segundo toque lo anula (sin diálogos del navegador). */
const avisosPorAnular = new Set<string>();

async function anularAviso(a: AvisoVisible): Promise<void> {
	const persona = app.sesion?.persona_habitual_id ?? a.persona_id;
	await encolar(
		app.bd,
		[
			{
				id: generarUlid(),
				tipo: 'aviso.anular',
				payload: { aviso_id: a.id, anulado_por: persona, motivo: 'anulado por quien lo dejó' },
				entidad: 'aviso' as Entidad,
				registro_id: a.id,
				personas: [persona],
				resumen: { entidad: 'aviso', fecha: fechaDeProduccion(ahoraMs()), accion: 'anulo', texto: a.texto },
			},
		],
		[],
		new Date(ahoraMs()).toISOString(),
	);
	avisosPorAnular.delete(a.id);
	recalcularAvisos(await mutacionesDeAvisos(app.bd, app.preferencias.catalogos_hora_servidor));
	app.dispararSync();
	app.render();
}

async function entendido(a: AvisoVisible): Promise<void> {
	app.avisosEntendidos.add(a.id);
	await escribirConfig(app.bd, 'avisos_entendidos', [...app.avisosEntendidos]).catch(() => null);
	recalcularAvisos(app.mutacionesDeAvisos);
	app.render();
}

/** Los avisos arriba del inicio: el propio con «Anular», los ajenos con «Entendido». */
function franjaDeAvisos(cuerpo: HTMLElement): void {
	for (const a of app.avisos) {
		const caja = el('div', { class: a.propio ? 'aviso-turno propio' : 'aviso-turno' });
		caja.appendChild(el('div', { class: 'lb' }, a.propio ? 'Tu aviso' : `Aviso de ${nombreDe(a.persona_id)}`));
		caja.appendChild(el('div', { class: 'vl' }, a.texto));
		const pieDelAviso = el('div', { class: 'pie-aviso' });
		pieDelAviso.appendChild(el('small', undefined, `${cuandoCorto(a.registrado_en, ahoraMs())}${a.local ? ' · sin subir' : ''}`));
		const boton = el('button', { class: avisosPorAnular.has(a.id) ? 'chip sel' : 'chip', type: 'button' }, a.propio ? (avisosPorAnular.has(a.id) ? 'Tocá de nuevo para anular' : 'Anular') : 'Entendido');
		boton.onclick = () => {
			if (!a.propio) return void entendido(a);
			if (!avisosPorAnular.has(a.id)) {
				avisosPorAnular.add(a.id);
				app.render();
				return;
			}
			void anularAviso(a);
		};
		pieDelAviso.appendChild(boton);
		caja.appendChild(pieDelAviso);
		cuerpo.appendChild(caja);
	}
}

/**
 * Una apertura a una hora en que el silo o el trieur estaban con otro la rechaza el servidor
 * (`silo_ocupado`, `trieur_ocupado`, §5.2.9), y el rechazo llegaba después, sin decir cuál
 * (primer día real, 2/10: 10:15 en un trieur cuyo silo anterior cerró a las 10:29). El teléfono
 * solo conoce lo que él cargó y los abiertos del catálogo: avisa, no bloquea —si la oficina
 * corrigió ese cierre, acá no se ve—.
 */
function avisoDeChoque(b: BorradorAbrir): HTMLElement | null {
	if (b.abierto_ms === null) return null;
	const conocidos = [...silosAbiertos().map((s) => ({ ...s, cerrado_en: null })), ...app.silosCerrados];
	const choque = chocaLaApertura(conocidos, { silo_fisico_id: b.silo_fisico_id, trieur_id: b.trieur_id, abierto_en: instanteLocal(b.abierto_ms) });
	if (!choque) return null;
	const { con } = choque;
	const hasta = con.cerrado_en === null ? null : cuandoCorto(con.cerrado_en, ahoraMs());
	if (choque.motivo === 'silo_ocupado') {
		return flag(
			'warn',
			`A esa hora el silo ${numeroDeSilo(con)} todavía tenía el precinto ${con.precinto}.`,
			hasta === null
				? 'Sigue abierto: cerralo antes de abrir otro en el mismo silo, o la oficina no lo va a aceptar.'
				: `Se cerró ${hasta}: abrilo a esa hora o después, o la oficina no lo va a aceptar (salvo que corrija ese cierre).`,
		);
	}
	return flag(
		'warn',
		`A esa hora el trieur ${numeroDeTrieur(con.trieur_id)} tenía otro silo: un trieur, un silo.`,
		hasta === null
			? `El silo ${numeroDeSilo(con)} (precinto ${con.precinto}) sigue abierto: cerralo antes, o la oficina no lo va a aceptar.`
			: `El silo ${numeroDeSilo(con)} (precinto ${con.precinto}) estuvo abierto hasta ${hasta}: abrilo a esa hora o después, o la oficina no lo va a aceptar (salvo que corrija ese cierre).`,
	);
}

// ── Revisión y guardado ───────────────────────────────────────────────────

type Fila = [string, string, number | null];

/** Lo que dice una pesada en la revisión: el bruto y lo que entró desde la anterior. */
function textoDePesada(p: Pesada, cierre: boolean): string {
	const brutoG = (p.bruto_kg ?? 0) * 1000;
	const entro = brutoG - (p.anterior_g ?? 0);
	if (entro > 0) return `${kg(brutoG)} kg · ${kg(entro)} kg este turno`;
	return `${kg(brutoG)} kg · sin cambio${cierre ? '' : ', no registra turno'}`;
}

export function filasDe(b: BorradorProduccion): Fila[] {
	const pasos = pasosDe(b);
	const indice = (nombre: string) => {
		const i = pasos.indexOf(nombre);
		return i === -1 ? null : i;
	};
	const quien = b.personas[0] ? nombreDe(b.personas[0]) : '—';
	// En un teléfono personal el «quién» lo fija el enrolamiento y la fila no se
	// cambia —igual que en verde, donde a la dueña no se la saca—: si hoy carga
	// otra, lo hace desde SU teléfono o desde el muleto, que no tiene dueña y
	// pregunta. Con tope 1, «cambiar» reemplazaba a la dueña (visto el 10/9).
	const pasoQuien = conPersonaHabitual(b.personas) ? null : indice('quien');
	// Lo del silo sale de la FOTO del borrador, no de la proyección de ahora: si otro
	// teléfono lo pesó mientras esto se cargaba, la revisión tiene que mostrar lo que
	// se eligió, que es lo que el payload va a decir.
	const silo = 'silo_numero' in b ? { numero: b.silo_numero ?? '—', precinto: b.precinto ?? '—', neto_g: b.neto_visto_g } : null;
	switch (b.tipo) {
		case 'abrir':
			return [
				['Quién', quien, pasoQuien],
				['Silo', b.silo_fisico_id === null ? '—' : numeroDeSilo({ silo_fisico_id: b.silo_fisico_id }), indice('silo')],
				['Lote', b.lote_campo_id === null ? '—' : nombreDeLote(b.lote_campo_id), indice('lote')],
				['Trieur', b.trieur_id === null ? '—' : numeroDeTrieur(b.trieur_id), indice('trieur')],
				['Precinto', b.precinto, indice('precinto')],
				['Foto del precinto', (b.foto ?? null) !== null ? 'sacada' : 'sin foto', indice('foto')],
				['Tara base', `${b.tara_kg ?? 0} kg`, indice('tara')],
				['Abierto a las', b.abierto_ms === null ? '—' : cuandoCorto(instanteLocal(b.abierto_ms), ahoraMs()), indice('hora')],
			];
		case 'turno':
			return [
				// «Entrega» es la que firma las pesadas —en un teléfono personal, la dueña,
				// fija— y «Recibe» se corrige aparte, que es lo único que de verdad puede
				// cambiar (Codex, ronda del diff, 8/9).
				['Entrega', quien, pasoQuien],
				['Recibe', b.recibe_id ? nombreDe(b.recibe_id) : 'nadie', indice('recibe')],
				['Turno', b.turno ? NOMBRE_TURNO[b.turno] : '—', indice('turno')],
				['Hasta las', b.hasta_ms === null ? '—' : cuandoCorto(instanteLocal(b.hasta_ms), ahoraMs()), indice('hasta')],
				['Aviso para la que entra', textoValido(b.aviso_texto) ?? 'ninguno', indice('aviso')],
				...b.silos.map(
					(p, i): Fila => [`Silo ${p.silo_numero ?? '—'} · precinto ${p.precinto ?? '—'}`, `${textoDePesada(p, false)} · desde ${(p.inicio_en ?? '').slice(11)}`, indice(`bruto:${i}`)],
				),
			];
		case 'cerrar': {
			const brutoG = (b.bruto_kg ?? 0) * 1000;
			return [
				[`Silo ${silo?.numero ?? '—'}`, `precinto ${silo?.precinto ?? '—'}`, indice('abierto')],
				['Quién', quien, pasoQuien],
				['Peso final', textoDePesada(b, true), indice('bruto')],
				['Neto final', `${kg(Math.max(0, brutoG - (b.tara_base_g ?? 0)))} kg`, null],
				['Cerrado a las', b.cerrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.cerrado_ms), ahoraMs()), indice('hora')],
				// v6 (§12.4): la nota opcional del cierre viaja aparte, pero se ve y se cambia como lo demás.
				['Nota del silo', textoValido(b.nota_texto) ?? 'ninguna', indice('nota')],
			];
		}
		case 'descartar':
			return [
				[`Silo ${silo?.numero ?? '—'}`, `precinto ${silo?.precinto ?? '—'}`, indice('abierto')],
				['Quién', quien, pasoQuien],
				['Motivo', b.motivo ?? '—', indice('motivo')],
				// La hora viaja en el payload (`cerrado_en`): tiene que verse y poder corregirse.
				['Descartado a las', b.cerrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.cerrado_ms), ahoraMs()), indice('hora')],
			];
		case 'aviso':
			return [
				['Quién', quien, pasoQuien],
				['Aviso', textoValido(b.texto) ?? '— (escribí algo)', indice('texto')],
			];
		case 'nota':
			return [
				['Quién', quien, pasoQuien],
				['Silo', b.silo === null ? '—' : `${b.silo} · precinto ${b.precinto_del_silo ?? '—'}`, indice('silo_nota')],
				['Nota', textoValido(b.texto) ?? '— (escribí algo)', indice('texto')],
			];
		case 'carro':
			return [
				['Quién', quien, pasoQuien],
				['Carro', b.carro_id === null ? '—' : String((app.catalogos?.carros ?? []).find((c) => c.id === b.carro_id)?.numero ?? '—'), indice('carro')],
				['Viene de', (b.carro_id === null ? undefined : vieneDe(b.carro_id)) ?? 'sin viaje todavía', indice('carro')],
				['A las', b.registrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.registrado_ms), ahoraMs()), indice('hora')],
			];
		default: {
			const motivo = (app.catalogos?.motivos_parada ?? []).find((m) => m.id === b.motivo_parada_id)?.nombre ?? '—';
			const detalle = (b.nota ?? '').trim();
			return [
				['Quién', quien, pasoQuien],
				['Motivo', detalle ? `${motivo} · ${detalle}` : motivo, indice('motivo')],
				['Minutos · turno', `${b.minutos ?? 0} min · ${b.turno ? NOMBRE_TURNO[b.turno] : '—'}`, indice('minutos')],
				['Turno arrancó', b.registrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.registrado_ms), ahoraMs(), true), indice('turno')],
			];
		}
	}
}

function pRevisar(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion): void {
	pregunta(cuerpo, '¿Está todo bien?', 'Tocá una fila para cambiarla.');
	const resumen = el('div', { class: 'sum' });
	for (const [etiqueta, valor, paso] of filasDe(b)) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, etiqueta));
		textos.appendChild(el('div', { class: 'vl' }, valor));
		fila.appendChild(textos);
		if (paso !== null) {
			const cambiar = el('button', { class: 'fix', type: 'button' }, 'cambiar');
			cambiar.onclick = () => void irAlPaso(b, paso);
			fila.appendChild(cambiar);
		}
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);

	if (b.tipo === 'abrir') {
		const aviso = avisoDeChoque(b);
		if (aviso) cuerpo.appendChild(aviso);
	}
	if (b.tipo === 'turno' && !b.silos.some(dejaParcial)) {
		cuerpo.appendChild(flag('warn', 'Ningún silo cambió de peso.', 'No queda ninguna pesada registrada; el turno se entrega igual.'));
	}
	// Un silo no se cierra vacío (Agata, 14/9): un borrador viejo que llegue acá sin pesada no confirma.
	const cierreVacio = b.tipo === 'cerrar' && !dejaParcial(b) && (b.neto_visto_g === null || b.neto_visto_g <= 0);
	if (b.tipo === 'cerrar' && !dejaParcial(b)) {
		cuerpo.appendChild(
			cierreVacio
				? flag('stop', 'Un silo no se cierra vacío.', 'Volvé a la pesada: si tiene material, pesalo; si se abrió por error, la oficina lo anula.')
				: flag('warn', 'El mismo peso que la última pesada.', 'No se registra un turno nuevo: se cierra con ese neto.'),
		);
	}
	if (b.tipo === 'descartar') {
		cuerpo.appendChild(flag('stop', 'Descartar no tiene vuelta desde el teléfono.', 'Si fue un error, lo reabre la oficina.'));
	}
	// El boceto ofrece «Descartar» acá y en ningún otro lado: quien iba a cerrar el
	// silo se encuentra con que está contaminado y tiene que poder decirlo sin
	// rehacer la carga. Se conserva la foto del silo y la hora ya revisada. Va en el
	// cuerpo y no en el pie: tres botones en el pie no entran en un teléfono y el
	// «Mantené apretado» se partía en dos renglones (visto el 11/9).
	if (b.tipo === 'cerrar') {
		const tirar = el('button', { class: 'enlace', type: 'button' }, 'Está contaminado: descartar el silo');
		tirar.onclick = () => void pasarADescartar(b);
		cuerpo.appendChild(tirar);
	}

	pie(pieNodo, b);
	if (!cierreVacio) pieNodo.appendChild(botonMantener(b.tipo === 'descartar' ? 'Mantené para descartar' : 'Mantené apretado', 1500, () => void confirmar(b)));
}

/** El padre congelado al tomar la foto del silo, si todavía hay algo que esperar. */
function padreDe(padre: string | null): { padre_id?: string } {
	return padre === null ? {} : { padre_id: padre };
}

/**
 * El `parcial.crear` de una pesada, con su resumen para la bandeja. La jornada es
 * la del arranque del parcial (`fecha` se deriva de `inicio_en` en el servidor),
 * no la de ahora: una pesada de las 02:00 pertenece al día anterior.
 */
function parcialParaLaCola(
	p: Pick<PesadaDeSilo, 'parcial_id' | 'llenado_id' | 'precinto' | 'padre_mutacion_id'> & Pesada,
	mutacionId: string,
	payload: Record<string, unknown>,
	personas: string[],
): MutacionAEncolar {
	const brutoG = (p.bruto_kg ?? 0) * 1000;
	return {
		id: mutacionId,
		personas: [...personas],
		tipo: 'parcial.crear',
		payload,
		entidad: 'parcial' as Entidad,
		registro_id: p.parcial_id,
		// Regla 14: si el silo se abrió en este teléfono y todavía no subió, el
		// parcial espera su acuse. Con el silo ya en el servidor, no hay padre.
		...padreDe(p.padre_mutacion_id),
		resumen: {
			entidad: 'parcial',
			fecha: fechaDeProduccion(p.inicio_en === null ? ahoraMs() : msDeInstanteLocal(p.inicio_en)),
			precinto: p.precinto ?? '—',
			bruto_g: brutoG,
			del_turno_g: Math.max(0, brutoG - (p.anterior_g ?? 0)),
		},
	};
}

/**
 * Lo que una confirmación deja en la cola: una mutación por registro, en el orden
 * en que el servidor tiene que aplicarlas. El cambio de turno son tantos parciales
 * como silos; el cierre que pesa es su parcial y detrás el cierre, que lo espera
 * (regla 14) y lo lleva de testigo.
 */
export function paraLaCola(b: BorradorProduccion, campaniaId: string): MutacionAEncolar[] {
	// La jornada del registro, no la de ahora: una carga de las 02:00 pertenece al día
	// anterior (corte de las 06:00), y una parada de la noche anotada a las 06:10
	// también. Con `ahoraMs` quedaban en la jornada equivocada de «lo cargado».
	const comun = { id: b.mutacion_id, personas: [...b.personas] };
	switch (b.tipo) {
		case 'abrir':
			return [
				{
					...comun,
					tipo: 'llenado.abrir',
					payload: armarPayloadAbrir(b, campaniaId),
					entidad: 'llenado' as Entidad,
					registro_id: b.llenado_id,
					resumen: {
						entidad: 'llenado',
						fecha: fechaDeProduccion(b.abierto_ms ?? ahoraMs()),
						accion: 'abrio',
						silo: b.silo_fisico_id === null ? '—' : numeroDeSilo({ silo_fisico_id: b.silo_fisico_id }),
						precinto: b.precinto,
						neto_g: null,
						...(b.silo_fisico_id === null || b.trieur_id === null || b.abierto_ms === null
							? {}
							: { intervalo: { silo_fisico_id: b.silo_fisico_id, trieur_id: b.trieur_id, abierto_en: instanteLocal(b.abierto_ms) } }),
					},
				},
			];
		case 'turno': {
			const contexto = { turno: b.turno, fin_ms: b.hasta_ms, entregada_por: b.personas[0], recibida_por: b.recibe_id, nota: b.nota };
			// Un silo que no cambió no deja parcial: el servidor exige la cadena creciente.
			const pesadas = b.silos.filter(dejaParcial).map((p) => parcialParaLaCola(p, p.mutacion_id, armarPayloadParcial(p, contexto), b.personas));
			// v6 (§12.2): el aviso para la que entra, si hay, viaja en la misma confirmación.
			const texto = textoValido(b.aviso_texto);
			if (texto === null || !b.aviso_id || !b.aviso_mutacion_id) return pesadas;
			return [...pesadas, avisoParaLaCola(b.aviso_mutacion_id, b.aviso_id, campaniaId, texto, b.personas)];
		}
		case 'aviso':
			return [avisoParaLaCola(b.mutacion_id, b.aviso_id, campaniaId, b.texto, b.personas)];
		case 'nota':
			return [notaParaLaCola(b.mutacion_id, b.nota_id, b.llenado_id, b.precinto_del_silo, b.texto, b.personas, b.padre_mutacion_id)];
		case 'cerrar': {
			const fecha = fechaDeProduccion(b.cerrado_ms ?? ahoraMs());
			const pesa = dejaParcial(b);
			const cierre: MutacionAEncolar = {
				...comun,
				tipo: 'llenado.cerrar',
				payload: armarPayloadCerrar(b),
				entidad: 'llenado' as Entidad,
				registro_id: b.llenado_id as string,
				// El cierre lleva de testigo el último parcial: si es el que este mismo
				// cierre deja, lo espera; si es uno que todavía está en la cola, el servidor
				// no lo conoce y respondería `parciales_cambiaron`.
				...padreDe(pesa ? b.parcial_mutacion_id : b.padre_mutacion_id),
				resumen: {
					entidad: 'llenado',
					fecha,
					accion: 'cerro',
					silo: b.silo_numero ?? '—',
					precinto: b.precinto ?? '—',
					// Lo pesado al cerrar menos la tara: cero si se cerró vacío, que no es «sin
					// pesar» (Codex, 11/9). Sin peso —no debería pasar— queda la foto.
					neto_g: b.bruto_kg === null ? b.neto_visto_g : Math.max(0, b.bruto_kg * 1000 - (b.tara_base_g ?? 0)),
					...(b.intervalo ? { intervalo: b.intervalo } : {}),
				},
			};
			// v6 (§12.4): la nota del cierre, si hay, viaja detrás del cierre.
			const texto = textoValido(b.nota_texto);
			const nota = texto !== null && b.nota_id && b.nota_mutacion_id ? [notaParaLaCola(b.nota_mutacion_id, b.nota_id, b.llenado_id, b.precinto, texto, b.personas, b.padre_mutacion_id)] : [];
			if (!pesa) return [cierre, ...nota];
			// El parcial del cierre depende de lo mismo que el cierre habría dependido: la
			// última pesada si sigue en la cola (su `inicio_en` es el fin de esa pesada), o
			// la apertura. Si la pesada cae, cae todo, en vez de cerrar con un hueco.
			const parcial = parcialParaLaCola(
				{ parcial_id: b.parcial_id, llenado_id: b.llenado_id, precinto: b.precinto, padre_mutacion_id: b.padre_mutacion_id, ...pesadaDe(b) },
				b.parcial_mutacion_id,
				armarPayloadParcialDelCierre(b),
				b.personas,
			);
			return [parcial, cierre, ...nota];
		}
		case 'descartar':
			return [
				{
					...comun,
					tipo: 'llenado.descartar',
					payload: armarPayloadDescartar(b),
					entidad: 'llenado' as Entidad,
					registro_id: b.llenado_id as string,
					...padreDe(b.padre_mutacion_id),
					resumen: {
						entidad: 'llenado',
						fecha: fechaDeProduccion(b.cerrado_ms ?? ahoraMs()),
						accion: 'descarto',
						silo: b.silo_numero ?? '—',
						precinto: b.precinto ?? '—',
						neto_g: null,
						...(b.intervalo ? { intervalo: b.intervalo } : {}),
					},
				},
			];
		case 'carro':
			return [
				{
					...comun,
					tipo: 'cambio_carro.crear',
					payload: armarPayloadCarro(b, campaniaId),
					entidad: 'cambio_carro' as Entidad,
					registro_id: b.cambio_carro_id,
					resumen: {
						entidad: 'cambio_carro',
						fecha: fechaDeProduccion(b.registrado_ms ?? ahoraMs()),
						carro: String((app.catalogos?.carros ?? []).find((c) => c.id === b.carro_id)?.numero ?? '—'),
					},
				},
			];
		case 'parada':
			return [
				{
					...comun,
					tipo: 'parada.crear',
					payload: armarPayloadParada(b, campaniaId),
					entidad: 'parada' as Entidad,
					registro_id: b.parada_id,
					resumen: {
						entidad: 'parada',
						fecha: fechaDeProduccion(b.registrado_ms ?? ahoraMs()),
						motivo: (app.catalogos?.motivos_parada ?? []).find((m) => m.id === b.motivo_parada_id)?.nombre ?? '—',
						minutos: b.minutos ?? 0,
					},
				},
			];
	}
}

function avisoParaLaCola(mutacionId: string, avisoId: string, campaniaId: string, texto: string | null | undefined, personas: string[]): MutacionAEncolar {
	const ahora = ahoraMs();
	return {
		id: mutacionId,
		personas: [...personas],
		tipo: 'aviso.crear',
		payload: armarPayloadAviso(avisoId, campaniaId, texto, personas[0], ahora),
		entidad: 'aviso' as Entidad,
		registro_id: avisoId,
		resumen: { entidad: 'aviso', fecha: fechaDeProduccion(ahora), accion: 'dejo', texto: textoValido(texto) ?? '' },
	};
}

function pesadaDe(p: Pesada): Pesada {
	return { abierto_en: p.abierto_en, inicio_en: p.inicio_en, anterior_g: p.anterior_g, tara_base_g: p.tara_base_g, bruto_kg: p.bruto_kg };
}

/** Del cierre al descarte sin volver a elegir el silo: se hereda todo lo visto. */
async function pasarADescartar(b: BorradorCerrar): Promise<void> {
	const descarte = nuevoBorradorDescartar();
	descarte.personas = [...b.personas];
	descarte.llenado_id = b.llenado_id;
	descarte.version_base_id = b.version_base_id;
	descarte.silo_numero = b.silo_numero;
	descarte.precinto = b.precinto;
	descarte.neto_visto_g = b.neto_visto_g;
	descarte.intervalo = b.intervalo ?? null;
	descarte.padre_mutacion_id = b.padre_mutacion_id;
	descarte.cerrado_ms = b.cerrado_ms ?? ahoraMs();
	descarte.paso = pasosDe(descarte).indexOf('motivo');
	await descartarBorrador(b).catch(() => null);
	await guardarBorrador(descarte).catch(() => null);
	irA({ tipo: 'produccion', borrador: descarte });
}

export async function confirmar(b: BorradorProduccion): Promise<void> {
	if (comprometiendo.has(b.capture_id)) return;
	comprometiendo.add(b.capture_id);
	try {
		// La campaña es la que el borrador fijó al nacer, no la abierta de ahora: una
		// carga empezada antes del cambio de campaña pertenece a la suya. Y solo la
		// exigen los tres comandos que la llevan en el payload; las pesadas y el cierre
		// cuelgan del silo (Codex, ronda del diff, 8/9).
		const campania = ('campania_id' in b ? b.campania_id : null) ?? campaniaAbiertaId();
		if (campania === null && 'campania_id' in b) {
			irA({ tipo: 'produccion-inicio', aviso: 'No hay campaña abierta: avisale al encargado antes de seguir cargando.' });
			return;
		}
		// Todo lo que deja una confirmación entra en UNA transacción: el cierre nunca
		// queda en la cola sin el parcial que lo precede, ni un silo pesado sin el otro.
		// Una apertura sin foto y sin «seguir sin foto» —un borrador anterior a la v4.5 que ya
		// había pasado el precinto— vuelve al paso de la foto: la decisión la toma la encargada.
		if (b.tipo === 'abrir' && (b.foto ?? null) === null && !b.sin_foto) {
			b.paso = pasosDe(b).indexOf('foto');
			await guardarBorrador(b);
			app.render();
			return;
		}
		// Un cambio de turno o un cierre guardados antes de la v6 no traen los ids del aviso o la nota:
		// se generan una vez y se guardan antes de encolar.
		if (b.tipo === 'turno' && (!b.aviso_id || !b.aviso_mutacion_id)) {
			b.aviso_id = generarUlid();
			b.aviso_mutacion_id = generarUlid();
			await guardarBorrador(b);
		}
		if (b.tipo === 'cerrar' && (!b.nota_id || !b.nota_mutacion_id)) {
			b.nota_id = generarUlid();
			b.nota_mutacion_id = generarUlid();
			await guardarBorrador(b);
		}
		if ((b.tipo === 'aviso' || b.tipo === 'nota') && textoValido(b.texto) === null) {
			b.paso = pasosDe(b).indexOf('texto');
			await guardarBorrador(b);
			app.render();
			return;
		}
		// Un carro que dejó de poder elegirse mientras se revisaba (otro teléfono lo bajó, o se
		// anuló su viaje) vuelve a la grilla, que lo dice (Nicolás, 6/10).
		if (b.tipo === 'carro' && !carroVigente(b)) {
			b.paso = pasosDe(b).indexOf('carro');
			await guardarBorrador(b);
			app.render();
			return;
		}
		const mutaciones = paraLaCola(b, campania ?? '');
		// El listo dice lo que se revisó, tomado ANTES de encolar: después, la proyección y el
		// catálogo ya cuentan esta bajada, y «Viene de» decía «ya bajó · viaje 158» (8/10).
		const filas = filasDe(b).map(([etiqueta, valor]): [string, string] => [etiqueta, valor]);
		const fotoDelPrecinto = b.tipo === 'abrir' ? (b.foto ?? null) : null;
		// La foto se confirma en la MISMA transacción que encola la apertura (§5.3).
		await encolar(app.bd, mutaciones, [b.capture_id], new Date(ahoraMs()).toISOString(), fotoDelPrecinto === null ? [] : [fotoDelPrecinto.foto_id]);
		if (fotoDelPrecinto !== null) blobsEnMemoria.delete(fotoDelPrecinto.foto_id);
		// La proyección se rehace ANTES de volver al inicio: si esperara al sync, un
		// silo recién abierto no aparecería y el cierre recién hecho seguiría ofrecido.
		recalcularSilos(await mutacionesDeProduccion(app.bd, app.preferencias.catalogos_hora_servidor), await historiaDeProduccion(app.bd));
		recalcularAvisos(await mutacionesDeAvisos(app.bd, app.preferencias.catalogos_hora_servidor));
		if (b.tipo === 'abrir' && b.lote_campo_id !== null) {
			const { actualizarPreferencias } = await import('../datos/bd');
			await actualizarPreferencias(app.bd, { ultimo_lote_id: b.lote_campo_id });
			app.preferencias.ultimo_lote_id = b.lote_campo_id;
		}
		app.dispararSync();
		irA({ tipo: 'produccion-listo', borrador: b, filas });
	} catch (error) {
		if (error instanceof BorradorYaConfirmado) {
			await descartarBorrador(b).catch(() => null);
			irA({ tipo: 'produccion-inicio', aviso: 'Ese registro ya se mandó desde otra pestaña: no se mandó dos veces.' });
			return;
		}
		console.error('no se pudo guardar el registro de producción', error);
		irA({ tipo: 'produccion-inicio', aviso: 'No se pudo guardar. Probá de nuevo.' });
	} finally {
		comprometiendo.delete(b.capture_id);
	}
}

/** El motivo del primer rechazo entre las mutaciones que dejó este borrador, o null si ninguna cayó. */
function motivoDelRechazo(b: BorradorProduccion): string | null {
	let ids: string[];
	try {
		ids = paraLaCola(b, '').map((m) => m.id);
	} catch {
		return null;
	}
	for (const id of ids) {
		const motivo = app.rechazos.get(id);
		if (motivo !== undefined) return motivo;
	}
	return null;
}

/** La pantalla de «listo», con lo que quedó guardado —las filas de la revisión al confirmar— y la vuelta al inicio. */
export function pintarListoProduccion(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorProduccion, filas: Array<[string, string]>): void {
	const titulos: Record<BorradorProduccion['tipo'], string> = {
		abrir: 'Silo abierto',
		turno: b.tipo === 'turno' && b.silos.length > 1 ? 'Silos pesados, turno entregado' : 'Silo pesado, turno entregado',
		cerrar: 'Silo cerrado',
		descartar: 'Silo descartado',
		carro: 'Bajada de carro anotada',
		parada: 'Parada anotada',
		aviso: 'Aviso dejado',
		nota: 'Nota anotada',
	};
	cuerpo.appendChild(el('div', { class: 'done' }, titulos[b.tipo]));
	const resumen = el('div', { class: 'ticket' });
	for (const [etiqueta, valor] of filas) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);
	// Si el rechazo llega con la pantalla a la vista, se dice acá mismo y no solo con el «¡!» (2/10).
	const rechazo = motivoDelRechazo(b);
	if (rechazo !== null) {
		cuerpo.appendChild(flag('stop', 'La oficina no lo aceptó.', `${queMotivo(rechazo)}. Avisale al encargado: lo quita o lo rehace desde la configuración.`));
	} else {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, app.enLinea ? 'Subiendo.' : 'Sin señal: sube solo cuando haya.'));
	}
	if (b.tipo === 'abrir' && (b.foto ?? null) !== null) cuerpo.appendChild(el('div', { class: 'qsub' }, 'La foto del precinto sube aparte, después del silo.'));
	const otra = el('button', { class: 'btn go ancho', type: 'button' }, 'Otra cosa');
	otra.onclick = () => acciones.inicioProduccion();
	pieNodo.appendChild(otra);
}
